import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import Icon1 from "@assets/icons/Icon";
import TextField from "@components/ui/textfields/TextField";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { sessionsApi } from "@services/api/sessions";
import { bookingsApi } from "@services/api/bookings";
import type { Booking } from "types/booking";

const MIN_DAYS_OPTIONS = [
  { value: "0", label: "Aujourd'hui (pas de marge)" },
  { value: "1", label: "1 jour" },
  { value: "2", label: "2 jours" },
  { value: "3", label: "3 jours" },
  { value: "5", label: "5 jours" },
  { value: "7", label: "1 semaine" },
  { value: "14", label: "2 semaines" },
  { value: "21", label: "3 semaines" },
  { value: "30", label: "1 mois" },
];

const SR_HOURS_OPTIONS = [
  { value: "0.5", label: "30 minutes" },
  { value: "1", label: "1 heure" },
  { value: "2", label: "2 heures" },
  { value: "3", label: "3 heures" },
  { value: "4", label: "4 heures" },
  { value: "6", label: "6 heures" },
  { value: "8", label: "8 heures" },
  { value: "12", label: "12 heures" },
  { value: "24", label: "1 jour" },
  { value: "48", label: "2 jours" },
  { value: "72", label: "3 jours" },
  { value: "96", label: "4 jours" },
  { value: "120", label: "5 jours" },
  { value: "144", label: "6 jours" },
  { value: "168", label: "1 semaine" },
  { value: "336", label: "2 semaines" },
];

const SAFETY_MARGIN_OPTIONS = [
  { value: "0", label: "Pas de marge" },
  { value: "0.5", label: "30 minutes" },
  { value: "1", label: "1 heure" },
  { value: "1.5", label: "1h30" },
  { value: "2", label: "2 heures" },
  { value: "3", label: "3 heures" },
  { value: "4", label: "4 heures" },
];

const SR_BUFFER_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 heure" },
];

const SR_MAX_WAIT_OPTIONS = [
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 heure" },
  { value: "90", label: "1h30" },
  { value: "120", label: "2 heures" },
];

const MAX_DAYS_AHEAD_OPTIONS = [
  { value: "14", label: "2 semaines" },
  { value: "30", label: "1 mois" },
  { value: "60", label: "2 mois" },
  { value: "90", label: "3 mois" },
  { value: "120", label: "4 mois" },
  { value: "180", label: "6 mois" },
  { value: "365", label: "1 an" },
];

/** Generates time options every 15 min between two hours (inclusive), e.g. 7h00, 7h15, 7h30, 7h45. */
function quarterHourOptions(startHour: number, endHour: number): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  for (let h = startHour; h <= endHour + 1e-9; h += 0.25) {
    const hours = Math.floor(h);
    const minutes = Math.round((h - hours) * 60);
    options.push({
      value: String(h),
      label: `${hours}h${String(minutes).padStart(2, "0")}`,
    });
  }
  return options;
}

const OPENING_HOUR_OPTIONS = quarterHourOptions(7, 10);

const CLOSING_HOUR_OPTIONS = quarterHourOptions(16, 20);

const LUNCH_START_OPTIONS = quarterHourOptions(11, 13);

const LUNCH_END_OPTIONS = quarterHourOptions(12, 14.5);

const MAX_PER_DAY_OPTIONS = [
  { value: "0", label: "Illimité" },
  { value: "2", label: "2 interventions" },
  { value: "3", label: "3 interventions" },
  { value: "4", label: "4 interventions" },
  { value: "5", label: "5 interventions" },
  { value: "6", label: "6 interventions" },
  { value: "8", label: "8 interventions" },
  { value: "10", label: "10 interventions" },
  { value: "12", label: "12 interventions" },
  { value: "15", label: "15 interventions" },
];

type HistoryEntry = { date: string; started: number; completed: number; abandoned: number };
type HistoryPeriod = "7d" | "30d" | "90d";

const HISTORY_PERIOD_LABELS: Record<HistoryPeriod, { label: string; days: number }> = {
  "7d": { label: "7 jours", days: 7 },
  "30d": { label: "30 jours", days: 30 },
  "90d": { label: "90 jours", days: 90 },
};

function SessionsChart({ history }: { history: HistoryEntry[] }) {
  const [period, setPeriod] = useState<HistoryPeriod>("30d");
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const buckets = useMemo(() => {
    const days = HISTORY_PERIOD_LABELS[period].days;
    return history.slice(-days);
  }, [history, period]);

  const totalStarted = buckets.reduce((s, b) => s + b.started, 0);
  const totalCompleted = buckets.reduce((s, b) => s + b.completed, 0);
  const values = buckets.map((b) => b.started);
  const completedValues = buckets.map((b) => b.completed);
  const maxStarted = Math.max(...values, 1);
  const maxCompleted = Math.max(...completedValues, 1);

  const chartW = 580;
  const chartH = 180;
  const leftMargin = 35;
  const rightMargin = 35;
  const plotW = chartW - leftMargin - rightMargin;

  const toX = (i: number) => values.length > 1 ? leftMargin + (i / (values.length - 1)) * plotW : leftMargin + plotW / 2;
  const toYStarted = (v: number) => chartH - (v / maxStarted) * (chartH - 20);
  const toYCompleted = (v: number) => chartH - (v / maxCompleted) * (chartH - 20);

  const points = values.map((v, i) => ({ x: toX(i), y: toYStarted(v) }));
  const completedPoints = completedValues.map((v, i) => ({ x: toX(i), y: toYCompleted(v) }));

  const buildPath = (pts: { x: number; y: number }[]) =>
    pts.reduce((acc, p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = pts[i - 1];
      const cpx = (prev.x + p.x) / 2;
      return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
    }, "");

  const pathD = buildPath(points);
  const completedPathD = buildPath(completedPoints);

  const fillD = points.length > 0
    ? `${pathD} L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`
    : "";

  // Y-axis labels
  const ySteps = 4;
  const yLabelsLeft = Array.from({ length: ySteps + 1 }, (_, i) => Math.round((maxStarted / ySteps) * i));
  const yLabelsRight = Array.from({ length: ySteps + 1 }, (_, i) => Math.round((maxCompleted / ySteps) * i));

  // X-axis labels
  const labeledBuckets = buckets.map((b, i) => {
    const days = buckets.length;
    const showLabel = days <= 7 || i === 0 || i === days - 1 || i % Math.ceil(days / 6) === 0;
    const d = new Date(b.date);
    const label = showLabel ? d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", "") : "";
    return { ...b, i, label };
  }).filter((b) => b.label);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || points.length === 0) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * chartW;
    let nearest = 0;
    let minDist = Infinity;
    points.forEach((p, i) => {
      const d = Math.abs(p.x - svgX);
      if (d < minDist) { minDist = d; nearest = i; }
    });
    setHoveredIdx(nearest);
  }, [points, chartW]);

  const handleMouseLeave = useCallback(() => setHoveredIdx(null), []);

  return (
    <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
      {/* Header */}
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col">
          <div className="font-semibold text-black">Sessions ouvertes</div>
          <div className="flex flex-row items-center gap-3">
            <span className="text-hx font-semibold" style={{ color: "#6366F1" }}>{totalStarted} ouvertes</span>
            <span className="text-hx font-semibold" style={{ color: "#22C55E" }}>{totalCompleted} converties</span>
          </div>
        </div>
        <div className="flex flex-row items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
          {(Object.keys(HISTORY_PERIOD_LABELS) as HistoryPeriod[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className="text-h4 font-medium px-2.5 py-1 rounded-md transition-colors"
              style={{
                background: period === p ? "#FFFFFF" : "transparent",
                color: period === p ? "#111827" : "#9CA3AF",
                boxShadow: period === p ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              }}
            >
              {HISTORY_PERIOD_LABELS[p].label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {totalStarted === 0 ? (
        <div className="flex items-center justify-center h-[180px] text-h3 text-neutral-300">
          Aucune session sur cette periode
        </div>
      ) : (
        <div className="w-full pt-[50px]">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${chartW} ${chartH + 30}`}
            width="100%"
            className="overflow-visible"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: "crosshair" }}
          >
            <defs>
              <linearGradient id="sessionChartFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366F1" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#6366F1" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {/* Grid lines (from left axis) */}
            {yLabelsLeft.map((label) => {
              const y = toYStarted(label);
              return (
                <g key={`grid-${label}`}>
                  <line x1={leftMargin} y1={y} x2={chartW - rightMargin} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                </g>
              );
            })}
            {/* Left Y-axis labels (indigo — sessions) */}
            {yLabelsLeft.map((label, i) => {
              const y = toYStarted(label);
              return (
                <text key={`left-${i}`} x={leftMargin - 8} y={y + 4} textAnchor="end" fontSize={11} fill="#6366F1">{label}</text>
              );
            })}
            {/* Right Y-axis labels (green — converted) */}
            {yLabelsRight.map((label, i) => {
              const y = toYCompleted(label);
              return (
                <text key={`right-${i}`} x={chartW - rightMargin + 8} y={y + 4} textAnchor="start" fontSize={11} fill="#22C55E">{label}</text>
              );
            })}
            {/* Fill */}
            {fillD && <path d={fillD} fill="url(#sessionChartFill)" />}
            {/* Started line */}
            {pathD && <path d={pathD} fill="none" stroke="#6366F1" strokeWidth={2.5} />}
            {/* Completed line */}
            {completedPathD && <path d={completedPathD} fill="none" stroke="#22C55E" strokeWidth={2} strokeDasharray="6 3" />}
            {/* Hover tooltip */}
            {hoveredIdx !== null && points[hoveredIdx] && (
              <>
                <line
                  x1={points[hoveredIdx].x} y1={0}
                  x2={points[hoveredIdx].x} y2={chartH}
                  stroke="#e5e7eb" strokeWidth={1} strokeDasharray="4 2"
                />
                <circle cx={points[hoveredIdx].x} cy={points[hoveredIdx].y} r={5} fill="#6366F1" stroke="white" strokeWidth={2} />
                <circle cx={completedPoints[hoveredIdx].x} cy={completedPoints[hoveredIdx].y} r={4} fill="#22C55E" stroke="white" strokeWidth={2} />
                <g transform={`translate(${Math.min(Math.max(points[hoveredIdx].x - 65, leftMargin), chartW - rightMargin - 130)}, ${points[hoveredIdx].y < 70 ? points[hoveredIdx].y + 15 : points[hoveredIdx].y - 60})`}>
                  <rect width={130} height={50} rx={8} fill="#1a1a1a" />
                  <text x={65} y={16} textAnchor="middle" fontSize={11} fill="#c8c8c8">
                    {new Date(buckets[hoveredIdx].date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", "")}
                  </text>
                  <text x={65} y={32} textAnchor="middle" fontSize={12} fill="#a5b4fc" fontWeight="600">
                    {buckets[hoveredIdx].started} ouvertes
                  </text>
                  <text x={65} y={46} textAnchor="middle" fontSize={12} fill="#86efac" fontWeight="600">
                    {buckets[hoveredIdx].completed} converties
                  </text>
                </g>
              </>
            )}
            {/* X labels */}
            {labeledBuckets.map((b) => (
              <text
                key={b.i}
                x={toX(b.i)}
                y={chartH + 20}
                textAnchor="middle"
                fontSize={11}
                fill="#a3a3a3"
              >
                {b.label}
              </text>
            ))}
          </svg>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-row items-center gap-4">
        <div className="flex flex-row items-center gap-1.5">
          <div className="w-3 h-0.5 rounded-full" style={{ background: "#6366F1" }} />
          <span className="text-h4 text-neutral-400">Sessions ouvertes</span>
        </div>
        <div className="flex flex-row items-center gap-1.5">
          <div className="w-3 h-0.5 rounded-full border-dashed" style={{ borderTop: "2px dashed #22C55E" }} />
          <span className="text-h4 text-neutral-400">Converties</span>
        </div>
      </div>
    </div>
  );
}

const IFRAME_BASE = "https://booking.heric.hexaflow.fr";

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative rounded-lg overflow-hidden border border-neutral-200">
      <pre
        className="text-h4 bg-neutral-950 text-neutral-200 p-4 overflow-x-auto whitespace-pre-wrap break-all"
        style={{ fontFamily: "monospace", lineHeight: 1.6 }}
      >
        {code}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-h4 font-medium transition-colors"
        style={{ background: copied ? "#22c55e" : "#3f3f46", color: "#fff" }}
      >
        <Icon1 name={copied ? "done" : "copy"} size={12} color="#fff" />
        {copied ? "Copié" : "Copier"}
      </button>
    </div>
  );
}

export const PriseDeRDV = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const updateOrgData = useDataStore((state) => state.updateOrgData);

  const [minDaysAhead, setMinDaysAhead] = useState(
    String(orgData?.config?.booking_min_days_ahead ?? 1),
  );
  const [maxDaysAhead, setMaxDaysAhead] = useState(
    String(orgData?.config?.booking_max_days_ahead ?? 90),
  );
  const [maxPerDay, setMaxPerDay] = useState(
    String(orgData?.config?.booking_max_per_day ?? 0),
  );
  const [safetyMargin, setSafetyMargin] = useState(
    String(orgData?.config?.booking_safety_margin_hours ?? 1),
  );
  const [srMinHours, setSrMinHours] = useState(
    String(orgData?.config?.booking_sr_min_hours_ahead ?? 2),
  );
  const [srBuffer, setSrBuffer] = useState(
    String(orgData?.config?.sr_reception_buffer_minutes ?? 30),
  );
  const [srMaxWait, setSrMaxWait] = useState(
    String(orgData?.config?.sr_max_wait_minutes ?? 60),
  );
  const [capacitySR, setCapacitySR] = useState(
    String(orgData?.config?.booking_capacity_service_rapide ?? 0),
  );
  const [capacityMecanique, setCapacityMecanique] = useState(
    String(orgData?.config?.booking_capacity_mecanique ?? 0),
  );
  const [capacityDiagnostic, setCapacityDiagnostic] = useState(
    String(orgData?.config?.booking_capacity_diagnostic ?? 0),
  );
  const [capacityCarrosserie, setCapacityCarrosserie] = useState(
    String(orgData?.config?.booking_capacity_carrosserie ?? 0),
  );
  const [openingHour, setOpeningHour] = useState(
    String(orgData?.config?.booking_opening_hour ?? 8),
  );
  const [closingHour, setClosingHour] = useState(
    String(orgData?.config?.booking_closing_hour ?? 18),
  );
  const [lunchStart, setLunchStart] = useState(
    String(orgData?.config?.booking_lunch_start ?? 12),
  );
  const [lunchEnd, setLunchEnd] = useState(
    String(orgData?.config?.booking_lunch_end ?? 13),
  );
  const [googleReviewUrl, setGoogleReviewUrl] = useState(
    orgData?.config?.google_review_url || "",
  );
  const [askRestitutionDateLong, setAskRestitutionDateLong] = useState(
    orgData?.config?.booking_ask_restitution_date_long ?? false,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ total: number; completed: number; abandoned: number; in_progress: number; conversion_rate: number; avg_abandon_step: number } | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const orgId = orgData?.id ?? "";

  useEffect(() => {
    if (!orgId) return;
    sessionsApi.getStats(orgId).then(setStats).catch(() => {});
    sessionsApi.getHistory(orgId, 90).then(setHistory).catch(() => {});
  }, [orgId]);

  // Preview state
  const [previewKey, setPreviewKey] = useState(0);
  const [previewMode, setPreviewMode] = useState<"mobile" | "web">("mobile");
  const previewVersion = "3";
  const [embedTab, setEmbedTab] = useState<"card" | "widget">("card");

  const handleSave = async () => {
    if (!orgData) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateOrgData(orgData.id, {
        config: {
          booking_min_days_ahead: parseInt(minDaysAhead),
          booking_max_days_ahead: parseInt(maxDaysAhead),
          booking_max_per_day: parseInt(maxPerDay),
          booking_safety_margin_hours: parseFloat(safetyMargin),
          booking_sr_min_hours_ahead: parseFloat(srMinHours),
          sr_reception_buffer_minutes: parseInt(srBuffer),
          sr_max_wait_minutes: parseInt(srMaxWait),
          booking_capacity_service_rapide: parseInt(capacitySR),
          booking_capacity_mecanique: parseInt(capacityMecanique),
          booking_capacity_diagnostic: parseInt(capacityDiagnostic),
          booking_capacity_carrosserie: parseInt(capacityCarrosserie),
          booking_opening_hour: parseFloat(openingHour),
          booking_closing_hour: parseFloat(closingHour),
          booking_lunch_start: parseFloat(lunchStart),
          booking_lunch_end: parseFloat(lunchEnd),
          google_review_url: googleReviewUrl || undefined,
          booking_ask_restitution_date_long: askRestitutionDateLong,
        },
      });
      showNotification({ message: "Modifications enregistrées avec succès", type: "success" });
    } catch {
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({ message: errorMessage, type: "error" });
    }
    setIsSaving(false);
  };

  const resetPreview = useCallback(() => setPreviewKey((k) => k + 1), []);

  const cardCode = `<iframe
  id="heric-booking"
  src="${IFRAME_BASE}?org_id=${orgId}&mode=card&v=${previewVersion}"
  width="100%"
  height="600"
  style="border:none; border-radius:16px; box-shadow:0 4px 24px rgba(0,0,0,0.10);"
></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e.data?.type === 'booking-widget-resize')
    document.getElementById('heric-booking').style.height = e.data.height + 'px';
});
</script>`;

  const widgetCode = `<iframe
  id="heric-widget"
  src="${IFRAME_BASE}?org_id=${orgId}&mode=widget&v=${previewVersion}"
  style="position:fixed; bottom:0; right:0; border:none; z-index:9999; width:420px; height:80px; transition:height 0.3s ease;"
></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e.data.type === 'heric:state')
    document.getElementById('heric-widget').style.height = e.data.open ? '750px' : '80px';
});
</script>`;

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Prise de RDV</Text1>
          <Text2>Configuration du widget de réservation en ligne.</Text2>
          <Text3>
            Gérez les paramètres de réservation en ligne accessibles par vos clients
            depuis votre site web.
          </Text3>
        </div>

        {/* ── Stats ───────────────────────────────────────────── */}
        {stats && (
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Formulaires ouverts", value: stats.total, color: "#1E1E1E" },
              { label: "Demandes complétées", value: stats.completed, color: "#22c55e" },
              { label: "Abandonnés", value: stats.abandoned, color: "#f97316" },
              { label: "Taux de conversion", value: `${stats.conversion_rate}%`, color: "#5C6BFF" },
            ].map((s) => (
              <div key={s.label} className="flex flex-col gap-1 bg-white p-4 rounded-lg border-1">
                <span className="text-h4 text-neutral-400">{s.label}</span>
                <span className="text-h1 font-semibold" style={{ color: s.color }}>{s.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Sessions chart ──────────────────────────────────── */}
        {history.length > 0 && <SessionsChart history={history} />}

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Horaires d'ouverture (réception)</div>
          <div className="text-h4 text-neutral-400">
            Plage horaire pendant laquelle les clients peuvent déposer ou récupérer leur véhicule.
            Aucun créneau de réception ou restitution ne sera proposé en dehors de ces horaires.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DropDown
              label="Ouverture"
              value={openingHour}
              options={OPENING_HOUR_OPTIONS}
              onSelect={(v) => setOpeningHour(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Fermeture"
              value={closingHour}
              options={CLOSING_HOUR_OPTIONS}
              onSelect={(v) => setClosingHour(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>

          <div className="text-h4 font-medium text-black mt-2">Pause déjeuner (réception)</div>
          <div className="text-h4 text-neutral-400">
            Plage horaire de pause déjeuner pendant laquelle aucun créneau de réception ou restitution ne sera proposé.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DropDown
              label="Début"
              value={lunchStart}
              options={LUNCH_START_OPTIONS}
              onSelect={(v) => setLunchStart(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Fin"
              value={lunchEnd}
              options={LUNCH_END_OPTIONS}
              onSelect={(v) => setLunchEnd(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Délais minimum de réservation</div>

          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-black">Interventions classiques</div>
            <div className="text-h4 text-neutral-400">
              Nombre de jours minimum entre aujourd'hui et le premier créneau disponible.
              Par exemple, "2 jours" signifie que les clients ne pourront pas réserver avant après-demain.
            </div>
            <DropDown
              label="Marge avant premier créneau"
              value={minDaysAhead}
              options={MIN_DAYS_OPTIONS}
              onSelect={(v) => setMinDaysAhead(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>

          <div className="border-t border-neutral-100" />

          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-black">Service Rapide</div>
            <div className="text-h4 text-neutral-400">
              Délai minimum en heures avant le premier créneau disponible pour les interventions
              marquées "Service Rapide". Ce délai remplace le délai classique ci-dessus pour les créneaux SR.
              Par exemple, "2 heures" signifie que les clients pourront réserver un créneau SR au plus tôt 2 heures après l'heure actuelle.
            </div>
            <DropDown
              label="Marge avant créneau SR"
              value={srMinHours}
              options={SR_HOURS_OPTIONS}
              onSelect={(v) => setSrMinHours(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>

          <div className="border-t border-neutral-100" />

          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-black">Temps de réception (buffer)</div>
            <div className="text-h4 text-neutral-400">
              Temps entre l'arrivée du client et le début de l'intervention par le mécanicien
              (accueil, paperasse, déplacement du véhicule).
            </div>
            <DropDown
              label="Buffer réception"
              value={srBuffer}
              options={SR_BUFFER_OPTIONS}
              onSelect={(v) => setSrBuffer(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>

          <div className="border-t border-neutral-100" />

          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-black">Attente maximum (Service Rapide)</div>
            <div className="text-h4 text-neutral-400">
              Temps maximum qu'un client peut attendre avant que le mécanicien ne commence l'intervention.
              Si aucun créneau libre ne permet de démarrer dans ce délai, le créneau est marqué indisponible.
            </div>
            <DropDown
              label="Attente max SR"
              value={srMaxWait}
              options={SR_MAX_WAIT_OPTIONS}
              onSelect={(v) => setSrMaxWait(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Délai maximum de réservation</div>
          <div className="text-h4 text-neutral-400">
            Horizon maximum de réservation. Les clients ne pourront pas réserver au-delà de cette période.
          </div>
          <DropDown
            label="Horizon de réservation"
            value={maxDaysAhead}
            options={MAX_DAYS_AHEAD_OPTIONS}
            onSelect={(v) => setMaxDaysAhead(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Réservations en ligne — maximum par jour</div>
          <div className="text-h4 text-neutral-400">
            Au-delà de ce nombre de rendez-vous de <b>réception</b> sur une journée, le jour
            est marqué « complet » pour la réservation en ligne. ⚠️ Compte <b>tous</b> les RDV
            de réception du jour, y compris ceux importés de ServiceBox — mettez « Illimité »
            (ou une valeur élevée) si vous ne voulez pas que les imports saturent les journées.
          </div>
          <DropDown
            label="Maximum par jour"
            value={maxPerDay}
            options={MAX_PER_DAY_OPTIONS}
            onSelect={(v) => setMaxPerDay(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Capacité journalière par spécialité</div>
          <div className="text-h4 text-neutral-400">
            Nombre maximum d'interventions planifiées par jour pour chaque spécialité.
            Au-delà de cette limite, la journée sera marquée comme complète pour cette spécialité.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DropDown
              label="Service Rapide"
              value={capacitySR}
              options={MAX_PER_DAY_OPTIONS}
              onSelect={(v) => setCapacitySR(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Mécanique"
              value={capacityMecanique}
              options={MAX_PER_DAY_OPTIONS}
              onSelect={(v) => setCapacityMecanique(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Diagnostic"
              value={capacityDiagnostic}
              options={MAX_PER_DAY_OPTIONS}
              onSelect={(v) => setCapacityDiagnostic(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Carrosserie"
              value={capacityCarrosserie}
              options={MAX_PER_DAY_OPTIONS}
              onSelect={(v) => setCapacityCarrosserie(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Marge de sécurité (restitution)</div>
          <div className="text-h4 text-neutral-400">
            Temps supplémentaire ajouté à la durée estimée de l'intervention pour calculer
            l'heure de restitution la plus tôt possible. Par exemple, si l'intervention dure 1h30
            et la marge est de 1h, le client ne pourra pas choisir une restitution avant 2h30 après le dépôt.
          </div>
          <DropDown
            label="Marge après intervention"
            value={safetyMargin}
            options={SAFETY_MARGIN_OPTIONS}
            onSelect={(v) => setSafetyMargin(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        {/* ── Restitution logic + configurable option ───────────── */}
        <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Date de restitution</div>
          <div className="text-h4 text-neutral-400">
            Lors de la prise de RDV, ce que le client voit à l'étape « Restitution »
            dépend de la <span className="font-medium text-neutral-600">durée estimée</span> de
            l'intervention (réglée dans Paramètres → Interventions) :
          </div>

          {/* In-UI explanation of the 3 cases */}
          <div className="flex flex-col gap-2">
            {[
              {
                tag: "Courte (< 2h30)",
                color: "#16A34A",
                bg: "#F0FDF4",
                border: "#BBF7D0",
                text: "Le client indique s'il attend sur place (véhicule prêt le jour même) ou choisit une heure de récupération dans la journée.",
              },
              {
                tag: "Longue (≥ 2h30) ou diagnostic",
                color: "#DC2626",
                bg: "#FEF2F2",
                border: "#FECACA",
                text: askRestitutionDateLong
                  ? "Option activée : le client choisit lui-même une date de restitution (minimum selon la durée estimée)."
                  : "Par défaut : le client voit « La date de restitution vous sera communiquée par votre garage » (il ne choisit pas de date).",
              },
              {
                tag: "Durée inconnue (0)",
                color: "#525252",
                bg: "#FAFAFA",
                border: "#E5E5E5",
                text: "Le client choisit une date de récupération (minimum 2 jours ouvrés après le dépôt).",
              },
            ].map((c) => (
              <div key={c.tag} className="flex flex-col gap-1 p-2.5 rounded-lg" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
                <span className="text-h5 font-semibold" style={{ color: c.color }}>{c.tag}</span>
                <span className="text-h5" style={{ color: "#525252" }}>{c.text}</span>
              </div>
            ))}
          </div>

          <SwitchBrick
            checked={askRestitutionDateLong}
            onChange={(v) => setAskRestitutionDateLong(v)}
            title="Proposer une date de restitution pour les longues durées"
            description="Si activé, le client choisit une date de restitution même pour les interventions ≥ 2h30 (au lieu du message « communiquée par le garage »)."
          />
        </div>


        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Lien Google Avis</div>
          <div className="text-h4 text-neutral-400">
            Lien vers votre fiche Google Business pour recueillir les avis clients.
            Si renseigné, les clients ayant donné 5 étoiles verront une invitation à laisser un avis Google.
          </div>
          <TextField
            label="URL Google Avis"
            value={googleReviewUrl}
            onChange={(v: string) => setGoogleReviewUrl(v)}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2">
          {saveError && (
            <div className="text-h4 text-red-500 mt-2">{saveError}</div>
          )}
          <Button
            className="mt-4"
            label={isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
            onClick={handleSave}
            disabled={isSaving || !isOwnerOrAdmin(userData)}
          />
        </div>

        {/* ── Preview ─────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 mt-4">
          <div className="flex flex-col">
            <div className="text-h2 font-semibold text-black">Prévisualisation</div>
            <div className="text-h4 text-neutral-400 mt-0.5">
              Aperçu de la page de réservation tel que vos clients la verront.
            </div>
          </div>

            {/* Mobile / Web toggle + Version toggle */}
          <div className="flex flex-row items-center gap-3">
            <div className="flex flex-row gap-1 p-1 bg-neutral-100 rounded-lg w-fit">
              {(["mobile", "web"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => { setPreviewMode(mode); setPreviewKey((k) => k + 1); }}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-h4 font-medium transition-colors"
                  style={{
                    background: previewMode === mode ? "#fff" : "transparent",
                    color: previewMode === mode ? "#1E1E1E" : "#A1A1A1",
                    boxShadow: previewMode === mode ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  <Icon1 name={mode === "mobile" ? "smartphone" : "desktop"} size={12} color={previewMode === mode ? "#1E1E1E" : "#A1A1A1"} />
                  {mode === "mobile" ? "Mobile" : "Web"}
                </button>
              ))}
            </div>
          </div>

          {/* Mock browser frame */}
          <div className="flex flex-col rounded-xl overflow-hidden border border-neutral-200 shadow-sm">
            {/* Browser chrome */}
            <div className="flex flex-row items-center gap-2 px-4 bg-neutral-100 border-b border-neutral-200" style={{ height: 40 }}>
              <div className="flex flex-row gap-1.5">
                <div className="rounded-full bg-neutral-300" style={{ width: 10, height: 10 }} />
                <div className="rounded-full bg-neutral-300" style={{ width: 10, height: 10 }} />
                <div className="rounded-full bg-neutral-300" style={{ width: 10, height: 10 }} />
              </div>
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-row items-center gap-1.5 px-3 bg-white rounded-md border border-neutral-200" style={{ height: 24 }}>
                  <Icon1 name="lock" size={9} color="#A1A1A1" />
                  <span className="text-h4 text-neutral-400" style={{ fontSize: 10 }}>
                    votresite.fr
                  </span>
                </div>
              </div>
              <button
                onClick={resetPreview}
                className="flex items-center justify-center rounded-md hover:bg-neutral-200 transition-colors"
                style={{ width: 26, height: 26 }}
                title="Réinitialiser la prévisualisation"
              >
                <Icon1 name="restart" size={12} color="#A1A1A1" />
              </button>
            </div>

            {/* Widget preview area */}
            <div className="bg-neutral-50 flex justify-center" style={{ padding: previewMode === "mobile" ? "32px 16px" : "32px 24px" }}>
              <iframe
                key={previewKey}
                src={`${IFRAME_BASE}?org_id=${orgId}&mode=${previewMode === "mobile" ? "page" : "card"}&v=${previewVersion}`}
                style={{
                  width: previewMode === "mobile" ? 375 : "100%",
                  height: 700,
                  border: previewMode === "mobile" ? "1px solid #E5E5E5" : "none",
                  borderRadius: 16,
                  display: "block",
                }}
                title="Prévisualisation"
              />
            </div>
          </div>
        </div>

        {/* ── Embed code ──────────────────────────────────────── */}
        <div className="flex flex-col gap-3 mt-2">
          <div className="flex flex-col">
            <div className="text-h2 font-semibold text-black">Intégrer sur votre site</div>
            <div className="text-h4 text-neutral-400 mt-0.5">
              Copiez le code et collez-le dans le HTML de votre site web.
            </div>
          </div>

          {/* Mode tabs */}
          <div className="flex flex-row gap-1 p-1 bg-neutral-100 rounded-lg w-fit">
            {(["card", "widget"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setEmbedTab(tab)}
                className="px-4 py-1.5 rounded-md text-h4 font-medium transition-colors"
                style={{
                  background: embedTab === tab ? "#fff" : "transparent",
                  color: embedTab === tab ? "#1E1E1E" : "#A1A1A1",
                  boxShadow: embedTab === tab ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                }}
              >
                {tab === "card" ? "Carte centrée" : "Widget flottant"}
              </button>
            ))}
          </div>

          {/* Description */}
          <div className="text-h4 text-neutral-500">
            {embedTab === "card"
              ? "À intégrer dans une section de votre page (ex. page 'Prendre RDV'). Vous contrôlez la position."
              : "À coller avant </body>. Un bouton flottant apparaît en bas à droite de toutes vos pages."}
          </div>

          <CodeBlock code={embedTab === "card" ? cardCode : widgetCode} />
        </div>
      </div>
    </div>
  );
};
